import { authGuard, NotFoundComponent, OAuthRouteComponent } from '@cumlaude/shared-authentication';
import { GebruikersComponent } from './beheer/gebruikers/gebruikers.component';
import { NewSessionComponent } from './core/new-session/new-session.component';
import { SomtodayComponent } from './core/somtoday/somtoday.component';
import { StartpaginaComponent } from './startpagina/startpagina.component';
import { Type } from '@angular/core';
import { DASHBOARD_REGISTRY, RegistryNode } from '@cumlaude/shared-dashboard-registry';
import { DASHBOARD_COMPONENTS } from './dashboard-components';
import { VestigingenComponent } from './beheer/vestigingen/vestigingen.component';
import { SchoolbreedComponent } from './beheer/schoolbreed/schoolbreed.component';
import { RollenComponent } from './beheer/rollen/rollen.component';
import { TeamComponent } from './beheer/team/team.component';
import { SectieComponent } from './beheer/sectie/sectie.component';
import { MentorComponent } from './beheer/docent-mentor/mentor.component';
import { BestuurComponent } from './beheer/bestuur/bestuur.component';
import { DocentComponent } from './beheer/docent-mentor/docent.component';
import { rolGuard } from './guards/rol-guard';
import { CijferkolommenComponent } from './beheer/cijferkolommen/cijferkolommen.component';
import { CijferkolommenDetailsComponent } from './beheer/cijferkolommen-details/cijferkolommen-details.component';
import { NoMatchedSharedRouteComponent } from './core/no-matched-shared-route/no-matched-shared-route.component';
import { sharedMatcher } from './guards/shared-matcher';
import { RechtenComponent } from './beheer/rechten/rechten.component';
import { VakkenUitsluitenComponent } from './beheer/vakken-uitsluiten/vakken-uitsluiten.component';
import { vaardigheden, vaardigheid2route } from './services/vaardigheden';
import { BasisvaardighedenBeheerComponent } from './beheer/basisvaardigheden/basisvaardigheden-beheer.component';
import { SupportComponent } from './beheer/support/support.component';
import { VestigingClusterVSOComponent } from './beheer/vestiging-cluster-vso/vestiging-cluster-vso.component';
import { AanwezigheidComponent } from './beheer/aanwezigheid/aanwezigheid.component';
import { InstellingBron, RCumLaudeModule, RRol } from '@cumlaude/service-contract';
import { AfwezigheidsredenenComponent } from './beheer/afwezigheidsredenen/afwezigheidsredenen.component';
import { saveGuard } from './beheer/save-guard/save-guard';
import { CumLaudeRoutes } from '@cumlaude/shared-components-menu';
import { UserRouteData } from './services/user-routes.service';
import { IconName } from '@cumlaude/shared-components-icons';

type HoofdpaginaPad = (typeof DASHBOARD_REGISTRY)[number]['path'];

/**
 * Iconen voor de verticale hoofdnavigatie, per hoofdpagina uit de DASHBOARD_REGISTRY.
 * `satisfies` zorgt dat de build faalt zodra de registry een hoofdpagina krijgt zonder icoon hier.
 */
const DASHBOARD_ICONEN = {
	onderwijsresultaten: 'school',
	prestatieanalyse: 'prestatieanalyse',
	basisvaardigheden: 'basisvaardigheden',
	cijfers: 'cijfers',
	doorstroom: 'doorstroom',
	aanwezigheid: 'aanwezigheid',
	leerling: 'gebruiker',
	details: 'details',
} satisfies Record<HoofdpaginaPad, IconName>;

/**
 * Genereert Angular-routes uit de DASHBOARD_REGISTRY boom.
 * - Tussenknopen (met `children`) krijgen een automatische redirect naar het eerste kind.
 * - Bladknopen krijgen het bijbehorende component uit `components`.
 * - Hoofdpagina's (top-levelnodes) krijgen hun icoon voor de verticale hoofdnavigatie uit DASHBOARD_ICONEN.
 */
function buildRoutes(nodes: ReadonlyArray<RegistryNode>, components: Record<string, Type<unknown>>, parentPath = ''): CumLaudeRoutes<UserRouteData> {
	return nodes.map((node) => {
		const { path, children, ...rest } = node;
		const fullPath = parentPath ? `${parentPath}/${path}` : path;
		const component = components[fullPath];
		const icon = parentPath === '' ? DASHBOARD_ICONEN[path as HoofdpaginaPad] : undefined;
		const data = { ...rest, ...(icon ? { icon } : {}) };

		if (children) {
			return {
				path,
				data: data as UserRouteData,
				...(component ? { component } : {}),
				children: [{ path: '', pathMatch: 'full', redirectTo: children[0].path }, ...buildRoutes(children, components, fullPath)],
			};
		}

		return {
			path,
			data: data as UserRouteData,
			component,
		};
	}) as CumLaudeRoutes<UserRouteData>;
}

export const APP_ROUTES: CumLaudeRoutes<UserRouteData> = [
	{
		path: 'oauth',
		children: [
			{ path: '', component: OAuthRouteComponent },
			{ path: 'new', component: NewSessionComponent },
			{ path: 'somtoday', component: SomtodayComponent },
		],
	},
	// Legacy dashboard redirecten naar de startpagina.
	{
		path: 'dashboard',
		children: [
			{ path: '', pathMatch: 'full', redirectTo: '/start' },
			{ path: '**', redirectTo: '/start' },
		],
	},
	{
		path: '',
		canActivate: [authGuard],
		canActivateChild: [authGuard],
		canMatch: [rolGuard],
		children: [
			{ path: '', pathMatch: 'full', redirectTo: 'start' },
			{
				path: 'start',
				data: { name: 'Startpagina', base: 'core' },
				component: StartpaginaComponent,
			},
			// De dashboard routes worden gegenereerd op basis van de DASHBOARD_REGISTRY, zodat ze automatisch in de navigatie verschijnen zodra ze in de registry staan.
			...buildRoutes(DASHBOARD_REGISTRY, DASHBOARD_COMPONENTS),
			{
				path: 'beheer',
				data: { root: true, name: 'Beheer' },
				children: [
					{ path: '', pathMatch: 'full', redirectTo: 'gebruikers' },
					{
						path: 'gebruikers',
						data: { name: 'Gebruikers', base: 'beheer', requiredRollen: [RRol.BEHEERDER], icon: 'gebruiker' },
						component: GebruikersComponent,
					},
					{
						path: 'vestigingen',
						data: { name: 'Vestigingen', base: 'beheer', requiredRollen: [RRol.BEHEERDER], icon: 'vestiging' },
						component: VestigingenComponent,
					},
					{
						path: 'rollen',
						data: { name: 'Rollen', base: 'beheer', icon: 'rol' },
						component: RollenComponent,
						children: [
							{ path: '', pathMatch: 'full', redirectTo: 'school' },
							{ path: 'school', data: { name: 'School', requiredRollen: [RRol.BEHEERDER] }, component: SchoolbreedComponent },
							{ path: 'team', data: { name: 'Team', requiredRollen: [RRol.BEHEERDER] }, component: TeamComponent },
							{ path: 'sectie', data: { name: 'Sectie', requiredRollen: [RRol.BEHEERDER] }, component: SectieComponent },
							{ path: 'docent', data: { name: 'Docent', requiredRollen: [RRol.BEHEERDER] }, component: DocentComponent },
							{ path: 'mentor', data: { name: 'Mentor', requiredRollen: [RRol.BEHEERDER] }, component: MentorComponent },
							{
								path: 'aanwezigheid',
								data: { name: 'Aanwezigheid', requiredRollen: [RRol.BEHEERDER] },
								component: AanwezigheidComponent,
							},
							{ path: 'bestuur', data: { name: 'Bestuur', requiredRollen: [RRol.BEHEERDER] }, component: BestuurComponent },
						],
					},
					{
						path: 'rechten',
						data: { name: 'Rechten', base: 'beheer', requiredRollen: [RRol.BEHEERDER], icon: 'doorstroom' },
						component: RechtenComponent,
					},
					{
						path: 'aanwezigheid',
						data: {
							name: 'Aanwezigheid',
							base: 'beheer',
							requiredRollen: [RRol.BEHEERDER],
							requiredModule: RCumLaudeModule.SCHOOLAANWEZIGHEID,
							icon: 'aanwezigheid',
						},
						component: AfwezigheidsredenenComponent,
						canDeactivate: [saveGuard],
					},
					{
						path: 'cijferinstellingen',
						data: { name: 'Cijferinstellingen', base: 'beheer', icon: 'instellingen' },
						children: [
							{ path: '', pathMatch: 'full', redirectTo: 'vakken_uitsluiten' },
							{
								path: 'vakken_uitsluiten',
								data: { name: 'Vakken', requiredRollen: [RRol.BEHEERDER], requiredBron: InstellingBron.Magister },
								component: VakkenUitsluitenComponent,
							},
							{
								path: 'cijferkolommen',
								data: { name: 'Cijferkolommen', requiredRollen: [RRol.BEHEERDER], requiredBron: InstellingBron.Magister },
								component: CijferkolommenComponent,
							},
							{
								path: 'cijferkolommen-details',
								data: { name: 'Cijferkolommen details', requiredRollen: [RRol.BEHEERDER], requiredBron: InstellingBron.Magister },
								component: CijferkolommenDetailsComponent,
							},
						],
					},
					{
						path: 'basisvaardigheden',
						data: { name: 'Basisvaardigheden', base: 'beheer', icon: 'basisvaardigheden' },
						children: [
							{ path: '', pathMatch: 'full', redirectTo: vaardigheid2route(vaardigheden[0]) },
							...vaardigheden.map((vaardigheid) => ({
								path: vaardigheid2route(vaardigheid),
								data: { name: vaardigheid, bv_nm_vaardigheid: vaardigheid, requiredRollen: [RRol.BEHEERDER] },
								component: BasisvaardighedenBeheerComponent,
								canDeactivate: [saveGuard],
							})),
						],
					},
					{
						path: 'vestiging-cluster-vso',
						data: { name: 'VSO', base: 'beheer', vso: true, requiredRollen: [RRol.BEHEERDER], icon: 'vso' },
						component: VestigingClusterVSOComponent,
						canDeactivate: [saveGuard],
					},
					{
						path: 'support',
						data: { name: 'Support', base: 'beheer', requiredRollen: [RRol.BEHEERDER], requiresSupport: true, icon: 'support' },
						component: SupportComponent,
					},
				],
			},
			{
				path: 'shared',
				component: NoMatchedSharedRouteComponent,
				canMatch: [sharedMatcher],
			},
		],
	},
	{
		canActivate: [authGuard],
		canActivateChild: [authGuard],
		path: '**',
		component: NotFoundComponent,
	},
];
